import React, { useEffect, useState } from 'react';
import { useUser } from '../context/UserContext';
import {
    MONETAG_COOLDOWN_MS,
    MONETAG_LAST_TRIGGER_KEY,
    MONETAG_ZONE_ID,
} from '../utils/monetagVignette';

function StatusRow({ label, ok, details }) {
    return (
        <div className="monetization-status-row">
            <div className="monetization-status-label">{label}</div>
            <div className={`monetization-status-pill ${ok ? 'is-ok' : 'is-bad'}`}>
                {ok ? 'OK' : 'Check needed'}
            </div>
            <div className="monetization-status-details">{details}</div>
        </div>
    );
}

function AdminAds() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [checks, setChecks] = useState({
        hasMetaTag: false,
        swReachable: false,
        swZoneConfigured: false,
        swScriptSourcePresent: false,
        swZoneIdDetected: null,
        cooldownMinutes: Math.round(MONETAG_COOLDOWN_MS / 60000),
        lastTriggerAt: null,
    });
    const { user, refreshUser } = useUser();

    const runChecks = async () => {
        setLoading(true);
        setError('');
        try {
            const metaTag = document.querySelector('meta[name="monetag"]');
            const hasMetaTag = Boolean(metaTag && metaTag.getAttribute('content'));

            let swReachable = false;
            let swZoneConfigured = false;
            let swScriptSourcePresent = false;
            let swZoneIdDetected = null;
            try {
                const swResp = await fetch('/sw.js', { cache: 'no-store' });
                swReachable = swResp.ok;
                if (swReachable) {
                    const swText = await swResp.text();
                    const zoneMatch = swText.match(/zoneId["']?\s*:\s*(\d+)/i);
                    swZoneIdDetected = zoneMatch ? zoneMatch[1] : null;
                    swZoneConfigured = Boolean(swZoneIdDetected);
                    swScriptSourcePresent = swText.includes('service-worker.min.js');
                }
            } catch (swErr) {
                swReachable = false;
            }

            let lastTriggerAt = null;
            try {
                const raw = localStorage.getItem(MONETAG_LAST_TRIGGER_KEY);
                const ts = Number(raw || 0);
                if (Number.isFinite(ts) && ts > 0) {
                    lastTriggerAt = new Date(ts).toLocaleString();
                }
            } catch (storageErr) {}

            setChecks({
                hasMetaTag,
                swReachable,
                swZoneConfigured,
                swScriptSourcePresent,
                swZoneIdDetected,
                cooldownMinutes: Math.round(MONETAG_COOLDOWN_MS / 60000),
                lastTriggerAt,
            });
        } catch (err) {
            setError(err.message || 'Failed to run monetization checks.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        runChecks();
    }, [user]);

    const handleRefresh = async () => {
        if (typeof refreshUser === 'function') {
            await refreshUser();
        }
        runChecks();
    };

    return (
        <div className="page admin-monetization-page">
            <header className="page-header">
                <span className="page-eyebrow">Admin</span>
                <h1>Monetization health</h1>
                <p className="page-subtitle">Monetag setup, verification, and local trigger controls for this frontend.</p>
            </header>

            <div className="panel admin-panel-surface">
                <div className="admin-actions">
                    <button className="btn btn-outline-secondary" onClick={handleRefresh} disabled={loading}>
                        {loading ? 'Refreshing…' : 'Refresh checks'}
                    </button>
                    <div className="text-muted small ms-auto">Zone: {MONETAG_ZONE_ID}</div>
                </div>

                {error && <div className="alert alert-danger mt-3">{error}</div>}

                <div className="monetization-status-list mt-3">
                    <StatusRow
                        label="Monetag verification meta"
                        ok={checks.hasMetaTag}
                        details={checks.hasMetaTag ? 'Meta tag is present in document head.' : 'Missing meta[name="monetag"] in index.html'}
                    />
                    <StatusRow
                        label="Service worker route"
                        ok={checks.swReachable}
                        details={checks.swReachable ? '/sw.js responds successfully.' : '/sw.js is not reachable.'}
                    />
                    <StatusRow
                        label="Service worker setup"
                        ok={checks.swZoneConfigured && checks.swScriptSourcePresent}
                        details={
                            checks.swZoneConfigured && checks.swScriptSourcePresent
                                ? `Zone ${checks.swZoneIdDetected} and Monetag worker script detected.`
                                : 'Missing zone config or Monetag worker script in /sw.js.'
                        }
                    />
                    <StatusRow
                        label="No-ads bypass (current admin user)"
                        ok={!user?.hasNoAds}
                        details={
                            user?.hasNoAds
                                ? 'This account has no-ads enabled; Monetag triggers are skipped.'
                                : 'This account allows ads; Monetag triggers can run.'
                        }
                    />
                    <StatusRow
                        label="In-app cooldown"
                        ok={checks.cooldownMinutes >= 10}
                        details={`Configured cooldown: ${checks.cooldownMinutes} minutes between automated triggers.`}
                    />
                </div>

                <div className="admin-meta-note mt-3">
                    <strong>Last local trigger:</strong> {checks.lastTriggerAt || 'No trigger recorded in this browser yet.'}
                </div>
            </div>

            <section className="panel admin-panel-surface mt-3">
                <h2 className="h5">Implementation notes</h2>
                <ul className="mb-0">
                    <li>Automated Monetag triggers run on landing, workspace entry, and review step.</li>
                    <li>A global 10-minute client-side cooldown limits repeat triggers.</li>
                    <li>Users with ad-removal purchase skip Monetag triggers in app logic.</li>
                    <li>Provider-side frequency behavior can still apply independently.</li>
                </ul>
            </section>
        </div>
    );
}

export default AdminAds;
